import type {
  LighthouseAudit,
  LighthouseCategory,
  LighthouseDetails
} from '../api/types/analysisApiTypes';

const MAX_CHAT_URL_LENGTH = 15_000;
const MAX_ISSUES = 30;
const MAX_TEXT_LENGTH = 900;

export type SpeedOptimizationIssue = {
  category: string;
  categoryScore: number | null;
  auditId: string;
  title: string;
  score: number | null;
  displayValue?: string;
  description?: string;
  explanation?: string;
  errorMessage?: string;
  details?: string;
};

export function isSpeedOptimizationIssue(audit: LighthouseAudit): boolean {
  if (
    audit.scoreDisplayMode === 'manual' ||
    audit.scoreDisplayMode === 'notApplicable' ||
    audit.scoreDisplayMode === 'informative'
  ) {
    return false;
  }

  return (
    audit.scoreDisplayMode === 'error' ||
    (audit.score !== null && audit.score !== undefined && audit.score < 0.9)
  );
}

const trimText = (value: unknown, length = MAX_TEXT_LENGTH): string | undefined => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const text = value.replace(/\s+/g, ' ').trim();
  return text.length > length ? `${text.slice(0, length)}…` : text;
};

function compactDetails(value: unknown, depth = 0): unknown {
  if (depth > 3 || value === null || typeof value !== 'object') {
    return typeof value === 'string' ? trimText(value, 500) : value;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map(item => compactDetails(item, depth + 1));
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([key, item]) => {
        if (
          ['data', 'dataUrl', 'screenshot', 'filmstrip'].includes(key) &&
          typeof item === 'string'
        ) {
          return false;
        }
        return item !== undefined;
      })
      .slice(0, 30)
      .map(([key, item]) => [key, compactDetails(item, depth + 1)])
  );
}

function auditDetailsText(details: unknown): string | undefined {
  if (!details || typeof details !== 'object') return undefined;
  const compact = JSON.stringify(compactDetails(details));
  return compact && compact.length > 4_000
    ? `${compact.slice(0, 4_000)}…`
    : compact;
}

export function getSpeedOptimizationIssues(
  details: LighthouseDetails
): SpeedOptimizationIssue[] {
  const categories = Object.values(details.categories ?? {}) as LighthouseCategory[];
  const issues: SpeedOptimizationIssue[] = [];
  const seen = new Set<string>();

  const addIssue = (
    audit: LighthouseAudit,
    auditId: string,
    category: string,
    categoryScore: number | null
  ) => {
    if (seen.has(auditId) || !isSpeedOptimizationIssue(audit)) return;
    seen.add(auditId);
    issues.push({
      category,
      categoryScore,
      auditId,
      title: trimText(audit.title) || auditId,
      score: audit.score ?? null,
      displayValue: trimText(audit.displayValue, 300),
      description: trimText(audit.description),
      explanation: trimText(audit.explanation),
      errorMessage: trimText(audit.errorMessage),
      details: auditDetailsText(audit.details)
    });
  };

  categories.forEach(category => {
    (category.auditRefs ?? []).forEach(ref => {
      const audit = details.audits?.[ref.id];
      if (!audit) return;
      addIssue(
        audit,
        ref.id,
        category.title || category.id || 'Unknown',
        category.score ?? null
      );
    });
  });

  Object.entries(details.audits ?? {}).forEach(([auditId, audit]) => {
    addIssue(audit, auditId, 'Lighthouse', null);
  });

  return issues.slice(0, MAX_ISSUES);
}

function issueBlock(issue: SpeedOptimizationIssue, index: number): string {
  const lines = [
    `${index + 1}. [${issue.category}] ${issue.title}`,
    `auditId: ${issue.auditId}`,
    `score: ${issue.score === null ? 'error' : `${Math.round(issue.score * 100)}/100`}`
  ];

  if (issue.displayValue) lines.push(`value: ${issue.displayValue}`);
  if (issue.description) lines.push(`description: ${issue.description}`);
  if (issue.explanation) lines.push(`explanation: ${issue.explanation}`);
  if (issue.errorMessage) lines.push(`error: ${issue.errorMessage}`);
  if (issue.details) lines.push(`details/table data: ${issue.details}`);

  return lines.join('\n');
}

function composePrompt(
  url: string,
  details: LighthouseDetails,
  issues: SpeedOptimizationIssue[]
): string {
  const categoryScores = Object.values(details.categories ?? {})
    .map(category => {
      const name = category.title || category.id || 'Unknown';
      const score =
        category.score === null || category.score === undefined
          ? 'نامشخص'
          : `${Math.round(category.score * 100)}/100`;
      return `${name}: ${score}`;
    })
    .join(' | ');

  return `نقش تو: متخصص ارشد بهینه‌سازی وب، Core Web Vitals و Lighthouse.

هدف: مشکلات واقعی صفحه زیر را از روی داده‌های Lighthouse تحلیل کن و برای هر مشکل راه‌حل قابل اجرا، همراه با مثال کد، ارائه بده.

آدرس صفحه: ${url}
امتیاز دسته‌ها: ${categoryScores || 'در دسترس نیست'}
نسخه Lighthouse: ${details.lighthouseVersion || 'نامشخص'}
زمان دریافت گزارش: ${details.fetchTime || 'نامشخص'}

مواردی که نیاز به بهینه‌سازی دارند:
${issues.map(issueBlock).join('\n\n')}

قواعد پاسخ:
1. پاسخ را فارسی بنویس و از کلی‌گویی پرهیز کن.
2. برای هر مورد به ترتیب زیر پاسخ بده: توضیح ساده مشکل، علت فنی محتمل، اثر آن بر سرعت/کاربر، راه‌حل مرحله‌به‌مرحله، و مثال واقعی قابل کپی.
3. اگر برای Audit داده جدولی یا details ارسال شده است، ستون‌ها و ردیف‌های آن را در تحلیل همان مشکل بررسی کن و به شواهد مشخص جدول ارجاع بده.
4. مثال را متناسب با نوع مشکل انتخاب کن: HTML، CSS، JavaScript، تصویر، فونت، Cache-Control، CDN، Nginx/Apache یا تنظیمات WordPress.
5. اگر مقدار یا داده کافی برای تشخیص قطعی وجود ندارد، آن را صریح بگو و دقیقاً بگو چه چیزی باید بررسی شود.
6. در پایان، موارد را بر اساس بیشترین اثر و کمترین هزینه اولویت‌بندی کن و یک چک‌لیست تست مجدد در Lighthouse و Core Web Vitals بده.
7. هیچ مشکل سالم یا صرفاً manual را به‌عنوان خطا معرفی نکن.`;
}

function toChatUrl(prompt: string): string {
  return `https://chatgpt.com/?hints=search&q=${encodeURIComponent(prompt)}`;
}

export function buildSpeedAdviceChatUrl(
  url: string,
  details: LighthouseDetails,
  issues: SpeedOptimizationIssue[]
): string {
  let prompt = composePrompt(url, details, issues);
  let chatUrl = toChatUrl(prompt);

  if (chatUrl.length > MAX_CHAT_URL_LENGTH) {
    prompt = composePrompt(url, details, issues.slice(0, 8));
    chatUrl = toChatUrl(prompt);
  }

  if (chatUrl.length > MAX_CHAT_URL_LENGTH) {
    prompt = composePrompt(url, details, issues.slice(0, 4));
    chatUrl = toChatUrl(prompt);
  }

  return chatUrl;
}
